/**
 * Message Queue Store — 游戏 iframe postMessage 通信的 FIFO 佇列 + ACK 機制
 *
 * 職責：
 * - 保證消息按順序送達 Cocos 游戲 iframe
 * - requiresAck=true 的消息等待對方回 ACK，超時自動重發（最多 2 次）
 * - 對方回 ACK status=error / 超過重試上限 → 呼叫 onMessageFailed callback
 * - iframe reload 時將 in-flight 消息重新插回隊頭
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// ==================== 類型定義 ====================

export interface QueueMessage {
  type: 'GAME_HOST'
  msgId: string
  action: string
  payload: Record<string, unknown>
  ts: number
  requiresAck?: boolean
}

export interface AckMessage {
  type: 'GAME_HOST'
  action: 'ACK'
  payload: {
    msgId?: string   // 新版 bridge（已修正）
    ackId?: string   // 舊版 bridge 相容欄位
    status: 'ok' | 'error'
    error?: string
    channel?: string
  }
  ts: number
}

export interface EnqueueOptions {
  immediate?: boolean
  dropIfNotReady?: boolean
}

// ==================== 配置常量 ====================

const ACK_TIMEOUT_MS = 3000
const MAX_RETRIES = 2
const RETRY_DELAYS = [300, 600] // 重試延遲（ms）
const MAX_QUEUE_SIZE = 200

// ==================== 私有模組變數（不放進響應式 State） ====================

let _timer: ReturnType<typeof setTimeout> | undefined
let _sendFn: ((message: QueueMessage) => void) | null = null
let _onFailedFn: ((message: QueueMessage, error: string) => void) | null = null

// ==================== 工具函數 ====================

export function generateMsgId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

export function createQueueMessage(
  action: string,
  payload: Record<string, unknown>,
  requiresAck = true,
): QueueMessage {
  return {
    type: 'GAME_HOST',
    msgId: generateMsgId(),
    action,
    payload,
    ts: Date.now(),
    requiresAck,
  }
}

export function isAckMessage(data: unknown): data is AckMessage {
  const p = (data as AckMessage)?.payload
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as AckMessage).type === 'GAME_HOST' &&
    (data as AckMessage).action === 'ACK' &&
    (typeof p?.msgId === 'string' || typeof p?.ackId === 'string')
  )
}

// ==================== Store ====================

export const useMessageQueueStore = defineStore('messageQueue', () => {
  // ── State ──────────────────────────────────────────────────────────
  const queue = ref<QueueMessage[]>([])
  const inFlight = ref<{ message: QueueMessage; sentAt: number; retries: number } | undefined>(
    undefined,
  )
  const ready = ref(false)
  const initialized = ref(false)

  // ── Getters ────────────────────────────────────────────────────────
  const queueLength = computed(() => queue.value.length)
  const isBusy = computed(() => !!inFlight.value)
  const isReady = computed(() => ready.value)

  // ── 內部工具 ───────────────────────────────────────────────────────
  function _checkInitialized() {
    if (!initialized.value) {
      throw new Error('[MessageQueue] Store not initialized. Call init() first.')
    }
  }

  function _clearTimer() {
    if (_timer !== undefined) {
      clearTimeout(_timer)
      _timer = undefined
    }
  }

  function _startTimer() {
    _clearTimer()
    _timer = setTimeout(() => handleTimeout(), ACK_TIMEOUT_MS)
  }

  function _addToQueue(message: QueueMessage) {
    if (queue.value.length >= MAX_QUEUE_SIZE) {
      const dropped = queue.value.shift()
      if (dropped) {
        console.warn('[web][MessageQueue] Queue full, dropped oldest message:', dropped.action)
        _onFailedFn?.(dropped, 'queue_full_dropped')
      }
    }
    queue.value.push(message)
  }

  // ── Actions ────────────────────────────────────────────────────────

  function init(options: {
    send: (message: QueueMessage) => void
    onMessageFailed?: (message: QueueMessage, error: string) => void
  }) {
    _sendFn = options.send
    _onFailedFn = options.onMessageFailed ?? null
    initialized.value = true
  }

  function sendNext() {
    _checkInitialized()
    if (!ready.value || inFlight.value) return
    if (queue.value.length === 0) return

    const message = queue.value.shift()!

    // requiresAck=false：直接發，不等 ACK，立即繼續
    if (message.requiresAck === false) {
      try {
        _sendFn?.(message)
      } catch (e) {
        console.error('[web][MessageQueue] send threw (fire-and-forget):', message.action, message.msgId, e)
        _onFailedFn?.(message, 'send_error')
      }
      sendNext()
      return
    }

    // requiresAck=true（預設）：設 inFlight + 啟動超時計時器
    // 先設 inFlight，再 send；若 send 拋錯需立即清 inFlight 並報失敗，否則隊列永久卡死
    inFlight.value = { message, sentAt: Date.now(), retries: 0 }
    try {
      _sendFn?.(message)
    } catch (e) {
      console.error('[web][MessageQueue] send threw:', message.action, message.msgId, e)
      inFlight.value = undefined
      _onFailedFn?.(message, 'send_error')
      sendNext()
      return
    }
    _startTimer()
  }

  function enqueue(message: QueueMessage, options?: EnqueueOptions) {
    _checkInitialized()
    const { immediate = false, dropIfNotReady = false } = options ?? {}

    if (immediate) {
      if (message.requiresAck !== false) {
        // immediate=true 繞過佇列，無法追蹤 ACK；強制降級為一般入列
        console.warn(
          '[web][MessageQueue] immediate=true with requiresAck=true is unsupported; falling back to queue:',
          message.action,
        )
      } else {
        if (ready.value) {
          _sendFn?.(message)
        } else if (!dropIfNotReady) {
          _addToQueue(message)
        }
        return
      }
    }

    _addToQueue(message)
    if (ready.value && !inFlight.value) sendNext()
  }

  function handleAck(ackMessage: AckMessage) {
    // 相容舊版 bridge（ackId）與新版 bridge（msgId）
    const { msgId, ackId, status, error } = ackMessage.payload
    const resolvedMsgId = msgId ?? ackId
    const current = inFlight.value

    if (!current || current.message.msgId !== resolvedMsgId) {
      console.debug('[web][MessageQueue] ACK not matched, ignoring:', resolvedMsgId)
      return
    }

    _clearTimer()

    if (status === 'ok') {
      inFlight.value = undefined
      sendNext()
    } else {
      // status=error：消費方明確拒絕，不重試
      const failedMessage = current.message
      inFlight.value = undefined
      _onFailedFn?.(failedMessage, error ?? 'consumer_rejected')
      sendNext()
    }
  }

  function handleTimeout() {
    const current = inFlight.value
    if (!current) return

    const { message, retries } = current

    if (retries < MAX_RETRIES) {
      current.retries = retries + 1
      current.sentAt = Date.now()
      console.warn(
        `[web][MessageQueue] Timeout, retrying (${current.retries}/${MAX_RETRIES}):`,
        message.action,
      )
      try {
        _sendFn?.(message)
      } catch (e) {
        console.error('[web][MessageQueue] send threw on retry:', message.action, message.msgId, e)
        inFlight.value = undefined
        _onFailedFn?.(message, 'send_error')
        sendNext()
        return
      }
      const delay = RETRY_DELAYS[retries] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1] ?? 300
      _timer = setTimeout(() => handleTimeout(), delay + ACK_TIMEOUT_MS)
    } else {
      console.error('[web][MessageQueue] Max retries exceeded:', message.action)
      inFlight.value = undefined
      _onFailedFn?.(message, 'max_retries_exceeded')
      sendNext()
    }
  }

  function setReady(value: boolean) {
    ready.value = value
    if (value) sendNext()
  }

  /** iframe reload 時：暫停佇列，將 in-flight 消息塞回隊頭以便就緒後重發 */
  function handleReload() {
    ready.value = false
    _clearTimer()
    const current = inFlight.value
    if (current) {
      queue.value.unshift(current.message)
      inFlight.value = undefined
    }
  }

  function clearQueue() {
    queue.value = []
    inFlight.value = undefined
    _clearTimer()
  }

  function reset() {
    clearQueue()
    ready.value = false
    initialized.value = false
    _timer = undefined
    _sendFn = null
    _onFailedFn = null
  }

  return {
    // State
    queue,
    inFlight,
    ready,
    initialized,
    // Getters
    queueLength,
    isBusy,
    isReady,
    // Actions
    init,
    enqueue,
    sendNext,
    handleAck,
    handleTimeout,
    setReady,
    handleReload,
    clearQueue,
    reset,
  }
})
